// covered-by: frontends/carrie/src/routes/ask-puntt/components/__tests__/HomeComposerModeToggle.test.tsx
import cx from "classnames";
import {
  useLayoutEffect,
  useRef,
  useState,
  type KeyboardEvent,
  type ReactNode,
} from "react";

export const SLIDING_SEGMENT_EASING = "cubic-bezier(0.16, 1, 0.3, 1)" as const;

const TRACK_INSET_PX = 2;
const TRACK_CLASS =
  "relative inline-flex h-8 items-stretch overflow-hidden rounded-xl bg-puntt-neutral-gray-3 p-0.5";
const THUMB_CLASS =
  "pointer-events-none absolute inset-y-0.5 z-[1] rounded-[10px] bg-base-white shadow-sm motion-reduce:transition-none";
const SEGMENT_CLASS =
  "relative z-[2] flex h-full shrink-0 cursor-pointer items-center gap-1.5 rounded-[10px] border-0 bg-transparent px-2.5 text-sm font-medium transition-colors duration-200 motion-reduce:transition-none";

export type SlidingSegment<T extends string> = {
  id: T;
  testId: string;
  content: ReactNode;
};

export function SlidingSegmentedControl<T extends string>({
  value,
  items,
  onChange,
  ariaLabel,
  thumbTestId,
  "data-testid": testId,
}: {
  value: T;
  items: readonly SlidingSegment<T>[];
  onChange: (id: T) => void;
  ariaLabel: string;
  thumbTestId: string;
  "data-testid": string;
}) {
  const trackRef = useRef<HTMLDivElement>(null);
  const segmentRefs = useRef(new Map<string, HTMLButtonElement>());
  const [thumb, setThumb] = useState({ left: 0, width: 0 });
  const ids = items.map((item) => item.id);

  useLayoutEffect(() => {
    const track = trackRef.current;
    const activeEl = segmentRefs.current.get(value);
    if (!track || !activeEl) return;

    const measure = () => {
      const trackBox = track.getBoundingClientRect();
      const activeBox = activeEl.getBoundingClientRect();
      const segLeft = activeBox.left - trackBox.left;
      const segRight = activeBox.right - trackBox.left;
      const left = Math.max(TRACK_INSET_PX, segLeft);
      const right = Math.min(trackBox.width - TRACK_INSET_PX, segRight);
      const width = Math.max(0, right - left);
      setThumb((current) =>
        current.left === left && current.width === width
          ? current
          : { left, width },
      );
    };

    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(track);
    observer.observe(activeEl);
    return () => observer.disconnect();
  }, [value]);

  const select = (id: T) => {
    // why: the inactive segment is removed from tab order, and a second click
    // on the active one must not notify the parent again.
    if (id === value) return;
    onChange(id);
    segmentRefs.current.get(id)?.focus();
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLButtonElement>) => {
    const index = ids.indexOf(value);
    if (index < 0) return;
    let next: T | null = null;
    switch (event.key) {
      case "ArrowRight":
      case "ArrowDown":
        next = ids[(index + 1) % ids.length] ?? null;
        break;
      case "ArrowLeft":
      case "ArrowUp":
        next = ids[(index - 1 + ids.length) % ids.length] ?? null;
        break;
      case "Home":
        next = ids[0] ?? null;
        break;
      case "End":
        next = ids[ids.length - 1] ?? null;
        break;
      default:
        break;
    }
    if (next == null) return;
    event.preventDefault();
    select(next);
  };

  return (
    <div
      ref={trackRef}
      role="radiogroup"
      aria-label={ariaLabel}
      data-testid={testId}
      className={TRACK_CLASS}
    >
      <span
        aria-hidden="true"
        data-testid={thumbTestId}
        className={THUMB_CLASS}
        style={{
          left: thumb.left,
          width: thumb.width,
          transition: `left 220ms ${SLIDING_SEGMENT_EASING}, width 220ms ${SLIDING_SEGMENT_EASING}`,
        }}
      />
      {items.map((item) => {
        const active = item.id === value;
        return (
          <button
            key={item.id}
            ref={(node) => {
              if (node) segmentRefs.current.set(item.id, node);
              else segmentRefs.current.delete(item.id);
            }}
            type="button"
            role="radio"
            data-testid={item.testId}
            aria-checked={active}
            tabIndex={active ? 0 : -1}
            data-state={active ? "on" : "off"}
            onClick={() => select(item.id)}
            onKeyDown={handleKeyDown}
            className={cx(
              SEGMENT_CLASS,
              active
                ? "text-puntt-neutral-gray-12"
                : "text-puntt-neutral-gray-10 hover:text-puntt-neutral-gray-12",
            )}
          >
            {item.content}
          </button>
        );
      })}
    </div>
  );
}
